import React, { useEffect, useState } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Dropdown, message, notification } from "antd";
import GenericTable from "../../../../Components/Tables/GenericTable";
import {
  PlusCircleOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import GenericModal from "../../../../Components/Modals/GenericModal";
import {
  getFuelBrands,
  updateFuelBrandStatus,
  deleteFuelBrand,
} from "../../../../API/Admin/SystemConfiguration/FuelBrands";
import usePageAccess from "../../../../Utlis/usePageAccess";
import { getInitials } from "../../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const mapFuelBrand = (item, index) => ({
  key: String(item.id ?? index),
  brandName: item.brand_name || "-",
  legalEntityName: item.legalentity_name || "-",
  logo: resolveAvatarUrl(item.avatar),
  status: capitalize(item.status) || "-",
});

const FuelBrands = ({ setBtnConfigs }) => {
  const { canEdit } = usePageAccess("system-configuration");
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleAdd = () => {
    navigate("/add-fuel-brand", {
      state: { from: "Fuel Brands" },
    });
  };

  useEffect(() => {
    setBtnConfigs([
      {
        text: "Add Fuel Brand",
        icon: <PlusCircleOutlined />,
        onClick: handleAdd,
      },
    ]);
  }, [setBtnConfigs]);

  const [FueldataSource, setDataSource] = useState([]);

  useEffect(() => {
    const fetchFuelBrands = async () => {
      try {
        const res = await getFuelBrands();
        const list = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setDataSource(list.map(mapFuelBrand));
        } else {
          message.error(getErrorMessage(res?.data, "Failed to load fuel brands."));
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fuel brands."),
        );
      }
    };

    fetchFuelBrands();
  }, []);

  const getUniqueFilters = (data, key) => {
    return [...new Set(data.map((item) => item[key]))]
      .filter((v) => v && v !== "-")
      .map((val) => ({
        text: val,
        value: val,
      }));
  };

  const statusFilters = getUniqueFilters(FueldataSource, "status");

  const handleView = (record) => {
    navigate("/fuel-brand-details", { state: { id: record.key } });
  };

  const handleEdit = (record) => {
    navigate("/edit-fuel-brand", { state: { id: record?.key } });
  };

  const isDeactivating = selectedRow?.status === "Active";

  const handleConfirmStatus = async (reason) => {
    if (!selectedRow) return;

    const newStatus = isDeactivating ? "Inactive" : "Active";
    const payload = isDeactivating
      ? { status: "inactive", inactive_reason: reason }
      : { status: "active" };

    try {
      const res = await updateFuelBrandStatus(selectedRow.key, payload);

      if (Math.floor(res?.status / 100) === 2) {
        setDataSource((prev) =>
          prev.map((item) =>
            item.key === selectedRow.key
              ? { ...item, status: newStatus }
              : item,
          ),
        );

        notification.success({
          message: "Status Updated",
          description:
            res?.data?.message ||
            `Fuel brand status has been changed to ${newStatus}.`,
          placement: "topRight",
        });
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to update fuel brand status."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to update fuel brand status.",
        ),
      );
    } finally {
      setIsConfirmOpen(false);
      setSelectedRow(null);
    }
  };

  const handleDeleteClick = (record) => {
    setDeleteTarget(record);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const res = await deleteFuelBrand(deleteTarget.key);

      if (Math.floor(res?.status / 100) === 2) {
        setDataSource((prev) =>
          prev.filter((item) => item.key !== deleteTarget.key),
        );
        message.success(res?.data?.message || "Fuel brand deleted successfully");
      } else {
        message.error(getErrorMessage(res?.data, "Failed to delete fuel brand."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to delete fuel brand."),
      );
    } finally {
      setIsDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  const FuelColumns = [
    {
      title: "Brand Name",
      dataIndex: "brandName",
      key: "brandName",
      render: (text, record) =>
        record.logo ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img
              src={record.logo}
              alt={text}
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
            <span>{text}</span>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
                background: getAvatarColor(text),
                flexShrink: 0,
              }}
            >
              {getInitials(text)}
            </span>
            <span>{text}</span>
          </div>
        ),
      enableSearch: true,
    },
    {
      title: "Legal Entity",
      dataIndex: "legalEntityName",
      key: "legalEntityName",
      enableSearch: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-tag ${status.toLowerCase()}`}>{status}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          canEdit && {
            key: "edit",
            label: "Edit",
            style: { color: "#4F46E5", fontWeight: 500 },
            icon: <EditOutlined />,
            onClick: () => {
              handleEdit(record);
            },
          },
          {
            key: "view",
            label: "View",
            icon: <EyeOutlined />,
            style: { color: "#000000", fontWeight: 500 },
            onClick: () => handleView(record),
          },
          canEdit && {
            key: "toggleStatus",
            label: record.status === "Active" ? "Deactivate" : "Activate",
            style: {
              color: record.status === "Active" ? "#DC2626" : "#16a34a",
              fontWeight: 500,
            },
            icon: <DeleteOutlined />,
            onClick: () => {
              setSelectedRow(record);
              setIsConfirmOpen(true);
            },
          },
          canEdit && {
            key: "delete",
            label: "Delete",
            style: { color: "#DC2626", fontWeight: 500 },
            icon: <DeleteOutlined />,
            onClick: () => handleDeleteClick(record),
          },
        ].filter(Boolean);
        return (
          <>
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  width: 24,
                  height: 24,
                }}
              >
                <BsThreeDotsVertical
                  style={{ fontSize: 20, color: "#4F46E5" }}
                />
              </span>
            </Dropdown>
          </>
        );
      },
    },
  ];

  return (
    <div className="fuel-brands">
      <GenericTable dataSource={FueldataSource} columns={FuelColumns} />
      <GenericModal
        open={isConfirmOpen}
        title={isDeactivating ? "Deactivate Brand" : "Activate Brand"}
        subtitle={
          isDeactivating
            ? `Are you sure you want to deactivate ${selectedRow?.brandName}?`
            : `Are you sure you want to activate ${selectedRow?.brandName}?`
        }
        highlightText={selectedRow?.brandName}
        showReason={true}
        textareaLabel={
          isDeactivating ? "Reason for Deactivation" : "Reason for Activation"
        }
        confirmText={
          isDeactivating ? "Confirm Deactivation" : "Confirm Activation"
        }
        cancelText="Cancel"
        iconColor="#ef4444"
        iconBgColor="#ef44441a"
        confirmBtnColor="#EA580C"
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmStatus}
      />
      <GenericModal
        open={isDeleteOpen}
        title="Delete Fuel Brand"
        subtitle={`Are you sure you want to delete ${deleteTarget?.brandName || "this fuel brand"}?`}
        highlightText={deleteTarget?.brandName || ""}
        showReason={false}
        confirmText="Delete"
        cancelText="Cancel"
        iconColor="#DC2626"
        iconBgColor="#FEE2E2"
        confirmBtnColor="#DC2626"
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default FuelBrands;
